<template>
	<CHeaderLayout
		v-if="globalStore.organization?.Plan.AllowWorkflowTasks"
		class="task-dashboard"
		has-cogicon
		:disable-scroll="showSkeleton"
		@show-account-menu="$emit('show-account-menu')"
	>
		<template #title>
			<h1 v-if="!!resources">{{ resources.MyTasks }}</h1>
			<div
				v-if="selectedGrouping"
				class="task-dashboard__grouping flex items-start"
			>
				<span>{{ resources.GroupTasksBy }}&nbsp;</span>
				<CSubtleSelect
					:options="groupingOptions"
					:value="selectedGrouping"
					append-to-body
					@change="handleSelectedChange"
				>
					<Inline svg="sort-down" />
				</CSubtleSelect>
			</div>
		</template>

		<div v-if="showSkeleton" class="task-dashboard__content task-dashboard__content--loading">
			<CTaskGroupSkeleton v-for="index of 3" :key="index" />
		</div>
		<div v-else-if="!globalStore.totalTaskCount" class="task-dashboard__no-tasks pb-6">
			<CNoTasksMessage :show-task-setup-info="isEditorOrAbove && !globalStore.taskViews.length" />
		</div>
		<div v-else class="task-dashboard__content pb-6">
			<!-- @vue-ignore :key -->
			<CTaskGroup
				v-for="groupingKey in sortedTasks.keys()"
				:key="groupingKey"
				:selected-grouping="selectedGrouping"
				:view-id="selectedGrouping === GroupingOptions.View ? groupingKey : null"
				:group-name="selectedGrouping === GroupingOptions.View ? getTaskSource(sortedTasks.get(groupingKey)[0]) : groupingKey"
				:actions="selectedGrouping === GroupingOptions.View ? (bulkActionsMap.get(groupingKey) || []) : null"
				:all-actions-loaded="selectedGrouping === GroupingOptions.View ? (allActionsLoadedMap.get(groupingKey) || false) : null"
				:task-count="sortedTasks.get(groupingKey).length"
				:number-of-groups="sortedTasks.size"
				:is-action-in-progress="isActionInProgress"
				:is-guest="isGuest"
				@perform-action="performBulkAction"
				@load-all-actions="handleLoadAllActions"
			>
				<!-- @vue-ignore :key -->
				<CTaskCard
					v-for="(task, i) of sortedTasks.get(groupingKey)"
					:key="`${groupingKey}-${task.EntityId}`"
					:number="i"
					:index="task"
					:get-entry="getEntry"
					:get-actions="getAllowedActions"
					:format-date="formatDate"
					:task-source="getTaskSource(task)"
					:entry-view-route="getEntryViewRoute(task, globalStore)"
					:action-status="getInlineActionStatus(task)"
					:due-date="getTaskDueDate(selectedGrouping === GroupingOptions.View ? groupingKey : task)"
					:static-due-date="getStaticDueDate(task)"
					:priority="getTaskPriority(selectedGrouping === GroupingOptions.View ? groupingKey : task)"
					:is-action-in-progress="isActionInProgress"
					@click="onTaskClick"
					@perform-action="performInlineAction"
					@actions-loaded="onActionsLoaded"
				/>
			</CTaskGroup>
		</div>
		<CBulkActionDialog
			v-if="!isGuest && showBulkActionDialog"
			:show="showBulkActionDialogContent"
			:action="bulkActionName"
			:selected-entries="entriesInView"
			:admin-api="adminApi"
			:role="bulkActionRoleId"
			:user-info="userInfo"
			:view-id="bulkActionViewId"
			:view-token="bulkActionViewToken"
			is-tasks
			@done="handleBulkActionComplete"
			@close="handleBulkActionDialogClose"
		/>
	</CHeaderLayout>
</template>

<script lang="ts">
	import { defineComponent, computed, ref, watch, onMounted, onUnmounted, inject } from 'vue';
	import { globalStore } from 'src/stores/global-store';
	import { injectAdminFormApi } from './utils/admin-api';
	import session from '@cognitoforms/api/services/session';
	import CHeaderLayout from 'src/components/app/HeaderLayout.vue';
	import CSubtleSelect from 'src/components/form-controls/SubtleSelect.vue';
	import CTaskGroup from './TaskGroup.vue';
	import CTaskGroupSkeleton from './TaskGroupSkeleton.vue';
	import CTaskCard from './TaskCard.vue';
	import CBulkActionDialog from '../entries/BulkActionDialog.vue';
	import { GroupingOptions, TaskOrganizationDate, TaskOrganizationPriority, useTaskManagement } from './utils/use-task-management';
	import { useTaskActions } from './utils/use-task-actions';
	import { getEntryId, getEntryViewRoute, getViewId } from './utils/use-task-utils';
	import CNoTasksMessage from 'src/components/info/NoTasksMessage.vue';
	import { checkRole } from 'src/views/admin/utils/role-check';
	import { handlePageAccessError } from '../router';
	import { websocketConnection } from 'src/utilities/websocket/websocket-connection-factory';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import { useRoute } from 'vue-router/composables';
	import { useFormLightbox } from './utils/use-form-lightbox';
	import { logout } from '@cognitoforms/api/services/session-service';
	import { EmbedContext } from '../../../../client/src/framework/public/form-handle';
	import { localizationResourcesKey } from 'src/utilities/injection-keys';
	import type { SubtleSelectOption } from 'src/components/form-controls/SubtleSelect.vue';
	import { setupKeyboardNavigation } from 'src/composables/use-keyboard-nav';

	const TASK_LIMIT = 1000;

	export default defineComponent({
		components: {
			CHeaderLayout,
			CSubtleSelect,
			CTaskGroup,
			CTaskGroupSkeleton,
			CTaskCard,
			CBulkActionDialog,
			CNoTasksMessage
		},
		async beforeRouteEnter(to, from, next) {
			try {
				await globalStore.isReady;
				if (!globalStore.organization.Plan.AllowWorkflowTasks)
					next({ name: 'home', params: { orgcode: to.params.orgcode, showTaskUpsell: 'show upsell' } });
				else if (to.query?.requiredUser && to.query.requiredUser !== globalStore.user.Email)
					logout().then(() => window.location.assign(to.path + '?entryId=' + to.query.entryId + '&viewId=' + to.query.viewId + '&requiredUser=' + to.query.requiredUser));
				else
					next();
			}
			catch (error) {
				handlePageAccessError(error, to, from, next);
			}
		},
		emits: ['show-account-menu'],
		setup() {
			const route = useRoute();
			const resources = inject(localizationResourcesKey);

			const firstDayOfWeek = session.value?.firstDayOfWeek;

			const { openFormLightbox, closeFormLightbox, adminApi } = useFormLightbox();

			const {
				tasks,
				taskViews,
				formsWithTaskViews,
				userInfo,
				loadTasksForViews,
				tasksSortedByView,
				tasksSortedByPriority,
				tasksSortedByDate,
				getTaskSource,
				getEntry,
				getTaskDueDate,
				getStaticDueDate,
				getTaskPriority,
				onTaskClick,
				selectedEntry,
				showLightbox,
				selectedViewId,
				taskRoleId,
				viewToken,
				dialogTitle,
				entryKey,
				inlineActionName,
				visibleTasks,
				showLocalizedError
			} = useTaskManagement({ globalStore, TASK_LIMIT, firstDayOfWeek, adminApi, openFormLightbox, closeFormLightbox });

			const {
				getAllowedActions,
				performInlineAction,
				getInlineActionStatus,
				performBulkAction,
				showBulkActionDialog,
				bulkActionName,
				bulkActionsMap,
				onActionsLoaded,
				handleLoadAllActions,
				allActionsLoadedMap,
				entriesInView,
				bulkActionRoleId,
				bulkActionViewId,
				bulkActionViewToken,
				handleBulkActionComplete,
				handleBulkActionDialogClose,
				showBulkActionDialogContent,
				isActionInProgress
			} = useTaskActions({ globalStore, adminApi, tasks, taskViews, formsWithTaskViews, userInfo, inlineActionName, selectedEntry, getEntry, onTaskClick, visibleTasks });

			const isEditorOrAbove = computed(() => checkRole(ClientRole.Editor));
			const isGuest = computed(() => globalStore.organization.Role === ClientRole.Guest);

			const allTasksLoaded = ref(false);
			const initDashboard = async () => {
				await injectAdminFormApi(globalStore.organization.FormClientApiKey).then(async () => {
					adminApi.value = window.AdminForm;
					await loadTasksForViews(taskViews.value.filter(taskView => taskView.Entries > 0).map(taskView => taskView.Id), true);
					allTasksLoaded.value = true;

					const initialEntryId = route.query.entryId;
					const initialViewId = route.query.viewId;

					let taskToOpenOnPageLoad;
					if (initialEntryId && initialViewId)
						taskToOpenOnPageLoad = tasks.value.find(idx => getEntryId(idx) === initialEntryId && getViewId(idx) === initialViewId);
					if (taskToOpenOnPageLoad)
						onTaskClick(taskToOpenOnPageLoad, false);

					setupKeyboardNavigation('.task-card');
				});
			};

			let entryViewSubscription = null;
			onMounted(async () => {
				document.body.classList.add('app-styled-form');
				document.documentElement.setAttribute('document-has', 'task-dashboard');

				entryViewSubscription = websocketConnection.subscribeToEvents('EntryView', async (updatedViewIds : string[]) => {
					const taskViewIdsToRefetchTasks = updatedViewIds.filter(viewId => taskViews.value.some(taskView => taskView.Id === viewId));
					await loadTasksForViews(taskViewIdsToRefetchTasks);
				});

				await initDashboard();
			});

			onUnmounted(() => {
				document.body.classList.remove('app-styled-form');
				document.documentElement.setAttribute('document-has', null);
				entryViewSubscription.unsubscribe();
			});

			const groupingOptions = computed<SubtleSelectOption[]>(() => {
				if (!resources)
					return [];

				return [
					{ label: resources.value.Type, value: GroupingOptions.View },
					{ label: resources.value.DueDate, value: GroupingOptions.DueDate },
					{ label: resources.value.Priority, value: GroupingOptions.Priority }
				];
			});

			const STORAGE_KEY = `taskDashboard_grouping_${globalStore.organization.Id}_${globalStore.user.Id}`;

			const selectedGrouping = ref(null);
			const getDefaultGrouping = () => {
				const storedGrouping = localStorage.getItem(STORAGE_KEY) as GroupingOptions;
				if (storedGrouping)
					return storedGrouping;

				const tasksSortedByDateKeys = Array.from(tasksSortedByDate.value.keys());
				const tasksSortedByPrioKeys = Array.from(tasksSortedByPriority.value.keys());

				const groupingOnlyUnscheduled = tasksSortedByDateKeys.length === 1 && tasksSortedByDateKeys[0] === TaskOrganizationDate.Unscheduled;
				const groupingOnlyUnprioritized = tasksSortedByPrioKeys.length === 1 && tasksSortedByPrioKeys[0] === TaskOrganizationPriority.Unprioritized;

				// Grouping should default to 'Due Date' if there are any due dates, else 'Priority' if there are any priorities, else default to 'Type'
				if (!storedGrouping && !groupingOnlyUnscheduled)
					return GroupingOptions.DueDate;
				else if (!storedGrouping && !groupingOnlyUnprioritized)
					return GroupingOptions.Priority;
				else
					return GroupingOptions.View;
			};

			watch(() => allTasksLoaded.value, () => {
				selectedGrouping.value = getDefaultGrouping();
			});

			const sortedTasks = computed(() => {
				if (selectedGrouping.value === GroupingOptions.View)
					return tasksSortedByView.value;
				else if (selectedGrouping.value === GroupingOptions.DueDate)
					return tasksSortedByDate.value;
				else
					return tasksSortedByPriority.value;
			});

			const showSkeleton = computed(() =>{
				return !allTasksLoaded.value;
			});

			const formatDate = async (formId: string, date: Date): Promise<string> => {
				const formDef = await adminApi.value.getFormDefinition(formId, EmbedContext.FormLightbox);
				const formsModel = formDef.model;
				return formsModel?.format(date, 'd');
			};

			const handleSelectedChange = (newSelection: GroupingOptions) => {
				selectedGrouping.value = newSelection;
				localStorage.setItem(STORAGE_KEY, newSelection);
			};

			return {
				resources,
				globalStore,
				groupingOptions,
				selectedGrouping,
				showSkeleton,
				GroupingOptions,
				adminApi,
				selectedEntry,
				showLightbox,
				selectedViewId,
				taskRoleId,
				viewToken,
				dialogTitle,
				entryKey,
				inlineActionName,
				showBulkActionDialog,
				bulkActionName,
				entriesInView,
				bulkActionRoleId,
				userInfo,
				bulkActionViewId,
				bulkActionViewToken,
				bulkActionsMap,
				handleBulkActionComplete,
				handleBulkActionDialogClose,
				showBulkActionDialogContent,
				getEntry,
				getTaskDueDate,
				getStaticDueDate,
				getTaskPriority,
				getAllowedActions,
				getTaskSource,
				getEntryViewRoute,
				onTaskClick,
				performBulkAction,
				performInlineAction,
				getInlineActionStatus,
				onActionsLoaded,
				handleLoadAllActions,
				allActionsLoadedMap,
				formatDate,
				tasks,
				isEditorOrAbove,
				isGuest,
				sortedTasks,
				handleSelectedChange,
				isActionInProgress,
				showLocalizedError
			};
		}
	});
</script>

<style lang="scss">
.task-dashboard {
	container: task-dashboard/ inline-size;

	&__header {

		:root .admin--nav-collapsed & {
			padding-left: calc($space__4 + var(--cogicon-space));
		}

		.i-chevron-down {
			width: $size__icon--small;
			height: $size__icon--small;
			color: var(--icon-subtle);
		}
	}

	&__no-tasks {
		max-width: 900px;
		margin: 0 auto;
	}

	&__content {
		width: 100%;
		max-width: 1500px;
		margin: 0 auto;

		&--loading {
			mask-image: linear-gradient(to bottom, black 40%, transparent 100%);
		}

		.admin--mobile & {
			padding-top: var(--spacing-3);
		}
	}

	.header-layout__content {

		/* stylelint-disable-next-line scss/at-rule-no-unknown */
		@container task-dashboard (max-width: 800px) {
			--content-top-padding: 0;
		}
	}
}

// Unwind the complex CSS used to achieve sliding sidebar and grid for global header
// This allows the mobile view to be simple full page scrollable which is desirable
// so the URL bar is able to be scrolled away on iPhone.
// WI to expand this to entire app: 49005
[document-has='task-dashboard'] .admin--mobile {

	&.admin--nav-collapsed {

		.app-container {
			width: initial;
			transform: none;
		}

		.nav {
			display: none;
		}
	}

	&.admin--app-nav,
	.app-container {
		display: block;
	}

	.app-container,
	.admin__scroll-container {
		overflow: visible;
	}

	.header-layout {
		display: block !important;
	}

	.header-layout__header {
		position: sticky;
		top: 0;
		z-index: $z1;
		background: var(--bg-primary);
		border-bottom-color: var(--border-primary);
	}
}

</style>